import enTranslation from '#/locales/en/translation.json';
import zhCNTranslation from '#/locales/zh-CN/translation.json';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {getNumberPositivityStatus} from '#/utils/number-utils';
import {formatDistance} from 'date-fns';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import {initReactI18next} from 'react-i18next';

export const defaultNS = 'translation';

export const resources = {
  'en': {
    translation: enTranslation,
  },
  'zh-CN': {
    translation: zhCNTranslation,
  },
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    defaultNS,
    resources,
    debug: false,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  });

i18n.services.formatter?.add('token', (value, _, options) => {
  const {decimals} = options;
  return Intl.NumberFormat('en-US', {maximumFractionDigits: decimals, minimumFractionDigits: decimals}).format(
    value / decimalsMultiplier,
  );
});

i18n.services.formatter?.add('thousands', (value, _, options) => {
  const {decimals} = options;
  return Intl.NumberFormat('en-US', {maximumFractionDigits: decimals, minimumFractionDigits: decimals}).format(value);
});

i18n.services.formatter?.add('percentage', (value, _, options) => {
  const {decimals = 2} = options;
  return Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number.isNaN(value) || value === 0 ? 0 : value);
});

i18n.services.formatter?.add('fixed', (value, _, options) => {
  const {decimals = 2} = options;
  return value.toFixed(decimals);
});

i18n.services.formatter?.add('number-en-compact-3-digits', value => {
  const formatter = Intl.NumberFormat('en', {notation: 'compact', minimumFractionDigits: 3});
  return formatter.format(value);
});

i18n.services.formatter?.add('number-plus-minus', value => {
  const numberPositivityStatus = getNumberPositivityStatus(value);

  if (numberPositivityStatus === 'positive') {
    return `+ ${value}`;
  }

  if (numberPositivityStatus === 'negative') {
    return `- ${Math.abs(value)}`;
  }

  return value;
});

i18n.services.formatter?.add('date-and-time', value => {
  const formatter = Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
  return formatter.format(value);
});

i18n.services.formatter?.add('date-distance', value => {
  const now = new Date();
  return formatDistance(value, now, {includeSeconds: true, addSuffix: true});
});

const countriesRequiringDefiniteArticle = [
  'BS',
  'KY',
  'CF',
  'JE',
  'KM',
  'CZ',
  'DO',
  'FK',
  'GM',
  'IM',
  'CI',
  'MV',
  'MH',
  'NL',
  'PH',
  'SB',
  'TC',
  'AE',
  'GB',
  'US',
  'VG',
];

i18n.services.formatter?.add('country-code-to-name', value => {
  const countryName = new Intl.DisplayNames(['en'], {type: 'region'}).of(value);
  if (countriesRequiringDefiniteArticle.includes(value)) {
    return `the ${countryName}`;
  }
  return countryName ?? '';
});

export default i18n;
